/* =========================================================
   GrooveVault – main.js
   Vanilla JS — no dependencies
   ========================================================= */

(function () {
  'use strict';

  /* -------------------------------------------------------
     Helpers
  ------------------------------------------------------- */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function formatMoney(cents) {
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars + ' CAD';
  }

  /* -------------------------------------------------------
     Toast Notifications
  ------------------------------------------------------- */
  var toastEl = null;
  var toastTimer = null;

  function showToast(message, type) {
    if (!toastEl) {
      toastEl = qs('#toast');
    }
    if (!toastEl) return;

    toastEl.textContent = message;
    toastEl.className = 'toast' + (type === 'error' ? ' toast--error' : '');

    requestAnimationFrame(function () {
      toastEl.classList.add('is-visible');
    });

    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 3500);
  }

  /* -------------------------------------------------------
     Cart Drawer State
  ------------------------------------------------------- */
  var cartDrawer = null;
  var cartOverlay = null;
  var cartDrawerBody = null;
  var cartDrawerFooter = null;
  var cartCountEl = null;

  function initCartDrawer() {
    cartDrawer = qs('#cart-drawer');
    cartOverlay = qs('#cart-overlay');
    cartDrawerBody = qs('#cart-drawer-body');
    cartDrawerFooter = qs('#cart-drawer-footer');
    cartCountEl = qs('#cart-count');
  }

  function openCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('is-open');
    cartOverlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    fetchAndRenderCart();
  }

  function closeCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartOverlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  /* -------------------------------------------------------
     Fetch Cart & Update Badge
  ------------------------------------------------------- */
  function fetchCart() {
    return fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  function updateCartBadge(count) {
    if (!cartCountEl) return;
    cartCountEl.textContent = count;
    if (count === 0) {
      cartCountEl.style.display = 'none';
    } else {
      cartCountEl.style.display = '';
    }
  }

  function refreshCartBadge() {
    fetchCart().then(function (cart) {
      updateCartBadge(cart.item_count);
    }).catch(function () {});
  }

  /* -------------------------------------------------------
     Render Cart Drawer Contents
  ------------------------------------------------------- */
  function fetchAndRenderCart() {
    if (!cartDrawerBody) return;
    cartDrawerBody.innerHTML = '<div class="cart-loading">Loading...</div>';

    fetchCart().then(function (cart) {
      renderCartDrawer(cart);
    }).catch(function () {
      cartDrawerBody.innerHTML = '<div class="cart-loading">Could not load cart.</div>';
    });
  }

  function renderCartDrawer(cart) {
    if (!cartDrawerBody || !cartDrawerFooter) return;

    updateCartBadge(cart.item_count);

    if (cart.item_count === 0) {
      cartDrawerBody.innerHTML =
        '<div class="cart-drawer-empty">' +
        '<p>Your cart is empty.</p>' +
        '<a href="/collections/all" class="btn btn--primary" style="margin-top:1rem;" onclick="window.GrooveVault.closeCart()">Browse Shop</a>' +
        '</div>';
      cartDrawerFooter.innerHTML = '';
      return;
    }

    var itemsHtml = cart.items.map(function (item) {
      var imgHtml = item.image
        ? '<img src="' + item.image + '" alt="' + escapeHtml(item.product_title) + '" class="cart-drawer-item-image" width="64" height="64" loading="lazy">'
        : '<div class="cart-drawer-item-image" style="background:var(--surface-2);"></div>';

      return (
        '<div class="cart-drawer-item" data-key="' + item.key + '">' +
          imgHtml +
          '<div class="cart-drawer-item-meta">' +
            '<div class="cart-drawer-item-title">' + escapeHtml(item.product_title) + '</div>' +
            '<div class="cart-drawer-item-price">' + formatMoney(item.price) + ' each</div>' +
            '<div class="cart-drawer-item-controls">' +
              '<div class="qty-control">' +
                '<button class="qty-btn" data-drawer-qty-key="' + item.key + '" data-delta="-1" aria-label="Decrease">−</button>' +
                '<span class="qty-display">' + item.quantity + '</span>' +
                '<button class="qty-btn" data-drawer-qty-key="' + item.key + '" data-delta="1" aria-label="Increase">+</button>' +
              '</div>' +
              '<button class="cart-item-remove" data-drawer-remove="' + item.key + '" style="margin-left:0.5rem;">Remove</button>' +
            '</div>' +
          '</div>' +
        '</div>'
      );
    }).join('');

    cartDrawerBody.innerHTML = itemsHtml;

    cartDrawerFooter.innerHTML =
      '<div class="cart-drawer-subtotal">' +
        '<span>Subtotal</span>' +
        '<span>' + formatMoney(cart.total_price) + '</span>' +
      '</div>' +
      '<a href="/checkout" class="btn btn--primary btn--block">Checkout</a>' +
      '<a href="/cart" class="btn btn--ghost btn--block" style="margin-top:0.5rem;">View Cart</a>';

    // Wire up drawer qty controls
    qsa('[data-drawer-qty-key]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-drawer-qty-key');
        var delta = parseInt(btn.getAttribute('data-delta'));
        var display = btn.parentElement.querySelector('.qty-display');
        var current = parseInt(display.textContent) || 1;
        var next = Math.max(0, current + delta);
        changeCartItem(key, next);
      });
    });

    // Wire up drawer remove buttons
    qsa('[data-drawer-remove]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-drawer-remove');
        changeCartItem(key, 0);
      });
    });
  }

  /* -------------------------------------------------------
     Cart AJAX Helpers
  ------------------------------------------------------- */
  function addToCart(variantId, quantity, productTitle) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
    }).then(function (r) {
      if (!r.ok) { return r.json().then(function (d) { throw new Error(d.description || 'Could not add to cart'); }); }
      return r.json();
    }).then(function () {
      showToast((productTitle || 'Item') + ' added to cart!');
      refreshCartBadge();
      openCartDrawer();
    }).catch(function (err) {
      showToast(err.message || 'Something went wrong.', 'error');
    });
  }

  function changeCartItem(key, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { return r.json(); })
    .then(function (cart) {
      renderCartDrawer(cart);
    }).catch(function () {
      showToast('Could not update cart.', 'error');
    });
  }

  /* -------------------------------------------------------
     Escape HTML helper
  ------------------------------------------------------- */
  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str || ''));
    return div.innerHTML;
  }

  /* -------------------------------------------------------
     Mobile Hamburger Nav
  ------------------------------------------------------- */
  function initMobileNav() {
    var hamburger = qs('#hamburger');
    var mobileNav = qs('#mobile-nav');
    var closeBtn = qs('#mobile-nav-close');

    if (!hamburger || !mobileNav) return;

    hamburger.addEventListener('click', function () {
      mobileNav.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    });

    function closeMobileNav() {
      mobileNav.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', closeMobileNav);
    }

    // Close on link click
    qsa('.mobile-nav-link', mobileNav).forEach(function (link) {
      link.addEventListener('click', closeMobileNav);
    });

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        closeMobileNav();
        closeCartDrawer();
      }
    });
  }

  /* -------------------------------------------------------
     Add-to-Cart Button Delegation
  ------------------------------------------------------- */
  function initAddToCart() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-add-to-cart-id]');
      if (!btn) return;
      if (btn.disabled) return;

      e.preventDefault();

      var variantId = parseInt(btn.getAttribute('data-add-to-cart-id'));
      var productTitle = btn.getAttribute('data-product-title') || 'Item';

      // Quantity from nearby qty input (product page)
      var form = btn.closest('form');
      var qty = 1;
      if (form) {
        var qtyInput = form.querySelector('[name="quantity"]');
        if (qtyInput) qty = parseInt(qtyInput.value) || 1;
      }

      btn.disabled = true;
      btn.textContent = 'Adding...';

      addToCart(variantId, qty, productTitle).finally(function () {
        btn.disabled = false;
        btn.textContent = 'Add to Cart';
      });
    });
  }

  /* -------------------------------------------------------
     Cart Open/Close Button Delegation
  ------------------------------------------------------- */
  function initCartTriggers() {
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-open]')) {
        openCartDrawer();
      }
      if (e.target.closest('[data-cart-close]')) {
        closeCartDrawer();
      }
    });
  }

  /* -------------------------------------------------------
     Sticky Nav Shadow on scroll
  ------------------------------------------------------- */
  function initNavScroll() {
    var nav = qs('#site-nav');
    if (!nav) return;
    window.addEventListener('scroll', function () {
      if (window.scrollY > 10) {
        nav.style.boxShadow = '0 4px 24px rgba(0,0,0,0.5)';
      } else {
        nav.style.boxShadow = '';
      }
    }, { passive: true });
  }

  /* -------------------------------------------------------
     Init
  ------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', function () {
    initCartDrawer();
    initCartTriggers();
    initAddToCart();
    initMobileNav();
    initNavScroll();
    refreshCartBadge();
  });

  /* -------------------------------------------------------
     Public API
  ------------------------------------------------------- */
  window.GrooveVault = {
    openCart: openCartDrawer,
    closeCart: closeCartDrawer,
    addToCart: addToCart,
    showToast: showToast
  };

})();
